import { Divider, Drawer, GradeRangeSelector } from '@/components';
import { FilterOption, OptionType } from '@/components/FlyerboardFilters/FilterOption';

import { BodyLargeEmphasized, ButtonFlatMedium, ButtonPrimaryMedium } from '@/design-system';
import colors from '@/theme/colors';
import { css } from '@emotion/css';
import { PropsWithChildren, Fragment } from 'react';
import { OwningTypeEnum } from '@/graphql/types';

type JarFilterDrawerProps = {
  isOpen: boolean;
  onToggle: (state: boolean) => void;
  filters: {
    cost: string[];
    provider: string[];
    types: string[];
    subscriptions: { id: string; type: string }[];
  };
  setFilters: (filters: any) => void;
  gradeFilter: { from?: string; to?: string };
  setGradeFilter: (gradeFilter: any) => void;
  loading: boolean;
  viewResultsLabel: string;
  resetFilters: () => void;
  viewResults: () => void;
  handleToggleOption: (section: string, value: string, type?: string) => void;
  handleGradeChange: (field: 'from' | 'to', value?: string) => void;
  generateProviderOptions: () => OptionType[];
  generateCostOption: () => OptionType[];
  generateTypesOptions: () => OptionType[];
  disableClearAllButton: boolean;
  grades: any[];
  lowGrade: string;
  highGrade: string;
  setDefaultGrades: () => void;
  renderSubscriptionList: () => React.ReactNode;
  subscriptionsLength: number;
};

export const JarFilterDrawer: React.FC<PropsWithChildren<JarFilterDrawerProps>> = props => {
  const {
    isOpen,
    onToggle,
    filters,
    gradeFilter,
    loading,
    viewResultsLabel,
    resetFilters,
    viewResults,
    handleToggleOption,
    handleGradeChange,
    generateProviderOptions,
    generateCostOption,
    generateTypesOptions,
    disableClearAllButton,
    grades,
    renderSubscriptionList,
    subscriptionsLength,
    lowGrade,
    highGrade,
  } = props;

  const sections = [
    {
      name: 'provider',
      options: generateProviderOptions(),
    },
    {
      name: 'cost',
      options: generateCostOption(),
    },
    {
      name: 'types',
      options: generateTypesOptions(),
    },
  ];

  const renderSection = ({
    name,
    label,
    content,
    isLast,
  }: {
    name: string;
    label: string;
    content: React.ReactNode;
    isLast: boolean;
  }) => (
    <fieldset
      key={name}
      className={styles.contentFieldset}
      aria-labelledby={`filter-${name}-label`}
    >
      <legend className={styles.legend}>
        <BodyLargeEmphasized className={styles.filterName} id={`filter-${name}-label`}>
          {label}
        </BodyLargeEmphasized>
      </legend>
      <div className={styles.optionsContainer}>{content}</div>
      {!isLast && (
        <div className={styles.divider}>
          <Divider />
        </div>
      )}
    </fieldset>
  );

  const FilterList = () => {
    const extraSectionAfter = 'cost';

    const items: {
      name: string;
      label: string;
      content: React.ReactNode;
    }[] = sections.flatMap(({ name, options }) => {
      const base = {
        name,
        label: name === 'provider' ? 'Posted by' : name,
        content: options.map(({ value, description, disabled = false }: OptionType) => {
          const isChecked = (filters as any)[name].includes(value);
          return (
            <Fragment key={`${name}-${value}`}>
              <FilterOption
                key={value}
                description={description}
                value={value}
                filterName={name}
                checked={isChecked}
                handleToggle={() => () => handleToggleOption(name, value)}
                disabled={disabled && !isChecked}
                placeholder={
                  name === 'provider' && value === OwningTypeEnum.organization
                    ? '(Organization)'
                    : undefined
                }
              />
            </Fragment>
          );
        }),
      };

      if (name === 'provider' && subscriptionsLength > 1) {
        return [
          base,
          {
            name: 'sentTo',
            label: 'Sent to',
            content: <>{renderSubscriptionList()}</>,
          },
        ];
      }

      if (name === extraSectionAfter) {
        return [
          base,
          {
            name: 'grade',
            label: 'Grade',
            content: (
              <GradeRangeSelector
                fromGrade={gradeFilter.from}
                toGrade={gradeFilter.to}
                onChange={handleGradeChange}
                grades={grades}
                initialValue={{ from: lowGrade, to: highGrade }}
              />
            ),
          },
        ];
      }

      return [base];
    });

    return (
      <div className={styles.filterList}>
        {items.map((item, index) =>
          renderSection({
            ...item,
            isLast: index === items.length - 1,
          })
        )}
      </div>
    );
  };

  return (
    <>
      <Drawer title="Filters" isOpen={isOpen} onToggle={() => onToggle(false)} full>
        <div className={styles.container}>
          <FilterList />
          <div className={styles.footer}>
            <ButtonFlatMedium
              data-testid="clearAllButton"
              onClick={resetFilters}
              disabled={disableClearAllButton}
            >
              Reset Filters
            </ButtonFlatMedium>
            <ButtonPrimaryMedium
              onClick={viewResults}
              data-testid="showResultsButton"
              loading={loading}
            >
              {viewResultsLabel}
            </ButtonPrimaryMedium>
          </div>
        </div>
      </Drawer>
    </>
  );
};

const styles = {
  filterList: css`
    flex-grow: 1;
  `,
  container: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
  `,
  footer: css`
    box-shadow: 0px -2px 8px rgba(0, 0, 0, 0.25);
    display: flex;
    position: sticky !important;
    background-color: ${colors.white};
    z-index: 1199;
    bottom: 0px;
    padding: 24px !important;
    margin-top: 25px;
    display: flex;
    justify-content: space-between !important;
    width: 100%;
  `,
  contentFieldset: css`
    padding: 24px 16px 0px 16px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    min-inline-size: 0;
    padding-block: 0;
  `,
  filterName: css`
    text-transform: capitalize;
  `,
  optionsContainer: css`
    padding-top: 7px;
  `,
  divider: css`
    padding-top: 15px;
  `,
  legend: css`
    margin: 0;
    padding: 24px 0px 0px;    
  `,
};
